import { Component, signal, inject, input, viewChild, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MessageService } from '@openng/optimus-ui/api';

import { FileUploadComponent } from 'src/app/components/file-upload/file-upload.component';
import { ImportDialogComponent } from 'src/app/components/import-dialog/import-dialog.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { isTreoEnvironment } from 'src/app/utils/environment.utils';

/**
 * Manual import entry point in the sidebar.
 *
 * Non-treo environments: a thin wrapper around FileUploadComponent — dropping a file imports
 * it immediately.
 *
 * TREO: shows an "Import files" button that opens the tabbed ImportDialogComponent. The dialog
 * only collects/bundles files and emits the final File; this component runs it through the
 * FileUploadComponent upload engine (queue + progress + import banner), the same path a normal
 * drop uses.
 */
@Component({
	selector: 'app-manual-import',
	standalone: true,
	imports: [FileUploadComponent, ImportDialogComponent],
	templateUrl: './manual-import.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './manual-import.component.scss',
})
export class ManualImportComponent {
	private messageService = inject(MessageService);

	private environmentService = inject(EnvironmentService);

	private destroyRef = inject(DestroyRef);

	// The hidden upload engine the dialog's emitted file is pushed through (queue/progress/banner).
	private fileUpload = viewChild.required<FileUploadComponent>('fileUpload');

	private importDialog = viewChild.required<ImportDialogComponent>('importDialog');

	// Environment availability - FileUploadComponent is disabled when no environment selected
	hasEnvironment = input<boolean>(false);

	importAcceptedTypes = signal<string>('.xml');

	isTreo = signal<boolean>(false);

	// Panel expansion state
	panelExpanded = signal<boolean>(false);

	constructor() {
		this.environmentService.environment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(env => {
			const treo = isTreoEnvironment(env);
			this.isTreo.set(treo);
			// treo imports a .json/.zip/.xlsx/.csv (via the dialog → hidden engine); others only .xml.
			this.importAcceptedTypes.set(treo ? '.json, .zip, .xlsx, .csv' : '.xml');
		});
	}

	/**
	 * Toggles the panel expansion
	 */
	public togglePanel(): void {
		this.panelExpanded.update(value => !value);
	}

	public openImportDialog(): void {
		this.importDialog().open();
	}

	/**
	 * Uploads the file the dialog emitted, via the same path a normal drop uses.
	 * @param file
	 */
	public onImportFile(file: File): void {
		this.fileUpload().enqueueAndUpload([file]);
	}

	/**
	 * Uploads several files the dialog emitted at once (RESI import: one zip per res.json).
	 * @param files
	 */
	public onImportFiles(files: File[]): void {
		if (files.length) {
			this.fileUpload().enqueueAndUpload(files);
		}
	}

	/**
	 * Handles successful upload
	 */
	public onUploadSuccess(): void {
		console.log('[ManualImportComponent] Upload successful!');
	}

	/**
	 * Handles upload error
	 * @param error
	 */
	public onUploadError(error: string): void {
		console.error('[ManualImportComponent] Upload error:', error);
		this.messageService.add({
			severity: 'error',
			summary: 'Upload Failed',
			detail: error,
			life: 5000,
		});
	}
}
